/* eslint-disable @next/next/link-passhref */
import { segment } from '@actions/analytics';
import Image from '@base/FutureImage';
import { BANNER_CLICKED, BANNER_VIEWED } from '@constants/segment';
import styled from '@emotion/styled';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useWindowSize from '@hooks/useWindowSize';
import { mediaQuery, Spacings } from '@styles/index';
import { ArrowButton } from 'aquilla/core/molecules/Carousel';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { GET_STARTED } from './constants';
import useSwipeDetect from './hooks/useSwipeDetect';
import { addBannerParamToURL } from './utils';

const SecContainer = styled.div`
  width: 100%;
  margin: ${Spacings.SPACING_6B} 0;
  overflow: hidden;

  ${mediaQuery.uptoTablet} {
    margin: ${Spacings.SPACING_8B} 0;
  }

  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_4B} 0;
  }
`;

const CarouselContainer = styled.div`
  width: var(--layout-width);
  position: relative;
  margin: auto;

  ${mediaQuery.uptoTablet} {
    width: calc(var(--layout-tab-width) - 100px);
  }

  ${mediaQuery.uptoMobile} {
    width: var(--layout-mweb-width);
  }
`;

const Carousel = styled.div`
  display: flex;
  flex-direction: row;
  grid-gap: 16px;
  height: 100%;
  width: 100%;
  transform: ${({ $midSlideIndex }) => {
    return `translateX(calc(-${$midSlideIndex} * (100% + 16px)))`;
  }};
`;

const CarouseItem = styled.div`
  width: var(--layout-width);
  aspect-ratio: 10/3;
  background-color: var(--color-i-violet-dark-1);
  flex: 1 0 var(--layout-width);
  border-radius: ${Spacings.SPACING_2B};
  overflow: hidden;
  border: 1px solid var(--color-divider);

  ${mediaQuery.uptoTablet} {
    width: calc(var(--layout-tab-width) - 100px);
    aspect-ratio: 100/33;
    flex: 1 0 calc(var(--layout-tab-width) - 100px);
  }

  ${mediaQuery.uptoMobile} {
    width: 328px;
    aspect-ratio: 250/89;
    flex: 1 0 var(--layout-mweb-width);
  }
`;

const StyledArrowButton = styled(ArrowButton)`
  z-index: 1;
  position: absolute;
  top: calc(50% - ${(props) => props.theme.measurements.spacing(24)});
  left: ${(props) =>
    props.type === 'prev' ? `${Spacings.SPACING_4B}` : 'initial'};
  right: ${(props) =>
    props.type === 'next' ? `${Spacings.SPACING_4B}` : 'initial'};
  border: 1px solid var(--color-i-divider);
  ${mediaQuery.uptoTablet} {
    display: none;
  }
`;

const ScreenWrapper = styled.div`
  height: 100%;
  &.desktop {
    display: block;
    img {
      height: 340px;
      width: 1136px;
    }
    ${mediaQuery.onlyTablet} {
      img {
        height: 220px;
        width: 668px;
      }
    }
    ${mediaQuery.uptoMobile} {
      display: none;
    }
  }
  &.mobile {
    display: none;
    ${mediaQuery.uptoMobile} {
      display: block;
    }
  }
`;

const StyledAnchor = styled.a`
  display: block;
`;

const NavButtonsContainer = styled.div`
  grid-column: ${({ $gridColumn }) => $gridColumn};
  padding-top: ${Spacings.SPACING_6B};
  padding-bottom: ${Spacings.SPACING_2B};
  display: flex;
  justify-content: center;
  margin-top: 0px;
  ${mediaQuery.uptoMobile} {
    padding-top: ${Spacings.SPACING_4B};
  }
`;

const NavButton = styled.div`
  width: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_2B};
  border-radius: 50%;
  background-color: ${({ $active }) =>
    $active ? 'var(--color-text-secondary)' : 'var(--color-divider)'};
  margin: 0 ${Spacings.SPACING_1B};
`;

const getPaddedArray = (array) => {
  const midIndex = Math.floor(array.length / 2);
  if (array.length === 1) return array;
  if (array.length >= 5) {
    return [...array.slice(midIndex + 1), ...array.slice(0, midIndex + 1)];
  }
  const paddedArray = [];
  for (let i = 0; i < Math.max(6, array.length * 2); ) {
    paddedArray.push(array[i % array.length]);
    i += 1;
  }
  return paddedArray;
};

const CarouselFeed = ({ block, source }) => {
  const { data: actualFeeds = [] } = block;
  const {
    query: { goalUID }
  } = useRouter();
  const feeds = getPaddedArray(actualFeeds);
  const [currentBanner, setCurrentBanner] = useState(0);
  const midSlideIndex =
    feeds.length % 2 === 0 &&
    (actualFeeds.length >= 5 || actualFeeds.length === 2)
      ? Math.floor(feeds.length / 2) - 1
      : Math.floor(feeds.length / 2);
  const carouseRef = useRef(null);
  const goalSegmentData = useGoalEventProps({ goalUID });
  const direction = useRef(null);
  const { isMobile, isTablet } = useWindowSize();
  const hideNavButton = feeds.length === 1;
  const bannerViewEventDone = useRef(false);
  const heroBanners = [];

  if (feeds?.length) {
    feeds?.forEach((banner) => {
      try {
        if (!banner.clickUrl.includes('/store')) {
          let lps = '';
          if (source === 'goal') lps = 'Free Home Page';
          if (source === 'store') lps = 'Store listing Page';
          if (!lps) {
            heroBanners.push(banner);
          } else {
            const url = new URL(banner.clickUrl);
            url.searchParams.append('lps', lps);
            url.searchParams.append('lpss', 'Banner');
            heroBanners.push({ ...banner, clickUrl: url.href });
          }
        } else {
          heroBanners.push(banner);
        }
      } catch (error) {
        heroBanners.push(banner);
      }
    });
  }

  const bannerViewed = () => {
    if (!bannerViewEventDone.current) {
      segment.track(BANNER_VIEWED, {
        ...goalSegmentData,
        use_segment_api: true
      });
      bannerViewEventDone.current = true;
    }
  };
  const callback = (entries) => {
    entries.forEach((entry) => {
      if (entry.intersectionRatio === 1) {
        bannerViewed();
      }
    });
  };
  useEffect(() => {
    const options = {
      rootMargin: '0px',
      threshold: 1.0
    };

    const observer = new IntersectionObserver(callback, options);
    const target = document.getElementById(GET_STARTED);
    if (target) {
      observer.observe(target);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    bannerViewEventDone.current = false;
  }, [goalUID]);

  useFirstRenderSkip(() => {
    bannerViewed();
  }, [goalSegmentData, bannerViewEventDone.current]);
  const bannerClicked = (e, banner_url, uid, title) => {
    segment.track(BANNER_CLICKED, {
      ...goalSegmentData,
      url: banner_url,
      banner_uid: uid,
      banner_title: title
    });
  };

  useEffect(() => {
    if (carouseRef.current) {
      carouseRef.current.addEventListener('transitionend', () => {
        if (direction.current === 'right') {
          const head = carouseRef.current.firstChild;
          carouseRef.current.removeChild(head);
          carouseRef.current.append(head);
        } else if (direction.current === 'left') {
          const tail = carouseRef.current.lastChild;
          carouseRef.current.removeChild(tail);
          carouseRef.current.prepend(tail);
        }
        carouseRef.current.style.transition = '';
        carouseRef.current.style.transform = `translateX(calc(-${midSlideIndex} * (100% + 16px)))`;
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTransition = () => {
    if (direction.current === 'right') {
      carouseRef.current.style.transition =
        'transform 0.5s cubic-bezier(0.2, 1, 0.3, 1) 0s';
      carouseRef.current.style.transform = `translateX(calc(-${
        midSlideIndex + 1
      } * (100% + 16px)))`;
    } else if (direction.current === 'left') {
      carouseRef.current.style.transition =
        'transform 0.5s cubic-bezier(0.2, 1, 0.3, 1) 0s';
      carouseRef.current.style.transform = `translateX(calc(-${
        midSlideIndex - 1
      } * (100% + 16px)))`;
    }
  };
  const handleNext = () => {
    direction.current = 'right';
    setCurrentBanner((prevIndex) => (prevIndex + 1) % actualFeeds.length);
    handleTransition();
  };

  const handlePrev = () => {
    direction.current = 'left';
    setCurrentBanner(
      (prevIndex) => (prevIndex - 1 + actualFeeds.length) % actualFeeds.length
    );
    handleTransition();
  };

  useSwipeDetect(
    carouseRef.current,
    (dir) => {
      if (isMobile || isTablet) {
        if (dir === 'left') {
          handleNext();
        } else if (dir === 'right') {
          handlePrev();
        }
      }
    },
    true
  );

  if (!feeds?.length) return null;
  return (
    <SecContainer id={GET_STARTED}>
      <CarouselContainer>
        {!hideNavButton ? (
          <StyledArrowButton type="prev" onClick={handlePrev} />
        ) : (
          <></>
        )}
        <Carousel ref={carouseRef} $midSlideIndex={midSlideIndex}>
          {heroBanners.map((feed = {}, i) => (
            <CarouseItem
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              onClick={(e) =>
                bannerClicked(e, feed.clickUrl, feed.uid, feed.title)
              }
            >
              <ScreenWrapper className="desktop">
                <StyledAnchor
                  rel="noopener noreferrer"
                  target="_blank"
                  href={feed.clickUrl}
                >
                  <Image
                    width={1136}
                    height={340.8}
                    alt=""
                    src={`${feed.bannerPhoto}`}
                    sizes="(max-width: 768px) 100vw,
                          (max-width: 1400px) 60vw,
                          60vw"
                  />
                </StyledAnchor>
              </ScreenWrapper>
              <ScreenWrapper className="mobile">
                <StyledAnchor
                  rel="noopener noreferrer"
                  target="_blank"
                  href={addBannerParamToURL(feed.clickUrl)}
                >
                  <Image
                    width={328}
                    height={116.768}
                    alt=""
                    src={feed.bannerAppPhoto}
                    priority={midSlideIndex === i}
                  />
                </StyledAnchor>
              </ScreenWrapper>
            </CarouseItem>
          ))}
        </Carousel>
        {!hideNavButton ? (
          <StyledArrowButton type="next" onClick={handleNext} />
        ) : (
          <></>
        )}
        {!hideNavButton && (
          <NavButtonsContainer>
            {actualFeeds.map((_, index) => (
              <NavButton
                key={actualFeeds[index]?.uid}
                $active={currentBanner === index}
              />
            ))}
          </NavButtonsContainer>
        )}
      </CarouselContainer>
    </SecContainer>
  );
};

export default CarouselFeed;
